import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { GridFilterModel } from "@mui/x-data-grid-premium";

// Context
import { CitContextProvider } from "@DataPages/context/cit/CitContext";

// Hooks
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useCitActivitiesPaginatedQuery } from "./components/CIT/hooks/useCitActivitiesPaginatedQuery";
import useCitExportHistorySubs from "@DataPages/hooks/financeManagement/cit/useCitExportHistorySubs";
import { useAllDealsPaginatedQuery } from "@DataPages/hooks/financeManagement/allDeals/useAllDealsPaginatedQuery.ts";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// API
import { getCitExportHistory, getCITSystemSettings } from "@DataPages/api/financeManagement/cit/queries";

// Layouts
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Components
import { CitModelHeader } from "./components/CIT/CitModelHeader";
import { CitModelAnalytics } from "./components/CIT/CitModelAnalytics";
import { CitModelTable } from "./components/CIT/CitModelTable";
import FinanceDealsBody from "./components/FinanceDeals/FinanceDealsBody";
import FniProductsBody from "@/DataPages/components/FniManagement/components/FniProducts/FniProductsBody";
import FnIDetailsBody from "@/DataPages/components/FniManagement/components/FniDetails/FniDetailsBody";
import TitlingBody from "@/DataPages/components/FniManagement/components/Titling/TitlingBody";
import TradeInPayoffsBody from "./components/Payoffs/TradeInPayoffsBody";
import AllDealsAnalyticsSection from "@DataPages/components/FniManagement/components/AllDeals/AllDealsAnalyticsSection";
import { AllDealsTableSection } from "@DataPages/components/FniManagement/components/AllDeals/AllDealsTableSection";
import { AllDealsContextProvider } from "@DataPages/context/cit/AllDealsContext";
import FinanceDealsAnalyticsSection from "@DataPages/components/FniManagement/components/FinanceDeals/FinanceDealsAnalyticsSection";

// API
import { getFinanceDeal } from "@DataPages/api/financeManagement/financeDeal";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Constants
import actionsList from "@Constants/actionsList";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IExportCitActivitiesSubscriptionResponse, IUpdateCitActivitiesSubscriptionResponse } from "@DataPages/api/financeManagement/cit";
import { ROAccessMap } from "@Common/interfaces/types";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { IFinanceDeal, IFinanceDealSubscriptionResponse } from "@DataPages/types/financeDeals/model";

enum ETabLabel {
	AllDeals = "All Deals",
	CIT = "Contracts In Transit (CIT)",
	FinanceDeals = "F&I Deal Data",
	FinanceDetails = "F&I Details",
	TradesInPayoffs = "Trades/Payoffs",
	Titling = "Titling",
	Cancellations = "Cancellations",
}

interface TabInfo {
	index: number;
	title: ETabLabel;
}

function getTabIndex(tabLabel: ETabLabel): number {
	return Object.values(ETabLabel).indexOf(tabLabel);
}

function isTabSelected(tabSelected: TabInfo, tab: ETabLabel): boolean {
	return tabSelected.title === tab;
}

interface ICitModelProps {
	selectedPayPeriod: IPeriodTreeSelection;
	isUpdatable: boolean;
	dataPresent: boolean;
	filterModel?: GridFilterModel;
	setFilterModel?: Dispatch<SetStateAction<GridFilterModel>>;
}

export default function FnIModel(props: Readonly<ICitModelProps>) {
	const { selectedPayPeriod, dataPresent, isUpdatable, filterModel, setFilterModel } = props;

	const { useAuth } = useAuthorization();
	const locationId = useLocationId();
	const queryClient = useQueryClient();

	const hasAllDealsAccess = useAuth(actionsList.VIEW_ALL_DEALS);
	const hasCITAccess = useAuth(actionsList.VIEW_CIT_REPORTS);
	const hasFinanceDealsAccess = useAuth(actionsList.VIEW_FINANCE_DEAL_DATA);
	const hasFinanceDetailsAccess = useAuth(actionsList.VIEW_FINANCE_DETAILS);
	const hasTradesInPayoffsAccess = useAuth(actionsList.VIEW_FINANCE_TRADES_PAYOFFS);
	const hasTitlingAccess = useAuth(actionsList.VIEW_FINANCE_TITLINGS);
	const hasCancellationsAccess = useAuth(actionsList.VIEW_CANCELLATIONS);

	const accessByTab = {
		[ETabLabel.AllDeals]: hasAllDealsAccess,
		[ETabLabel.CIT]: hasCITAccess,
		[ETabLabel.FinanceDeals]: hasFinanceDealsAccess,
		[ETabLabel.FinanceDetails]: hasFinanceDetailsAccess,
		[ETabLabel.TradesInPayoffs]: hasTradesInPayoffsAccess,
		[ETabLabel.Titling]: hasTitlingAccess,
		[ETabLabel.Cancellations]: hasCancellationsAccess,
	} satisfies ROAccessMap<ETabLabel>;

	const isTabEnabled = useCallback(
		(tab: ETabLabel) => accessByTab[tab],
		[
			hasAllDealsAccess,
			hasCITAccess,
			hasFinanceDealsAccess,
			hasFinanceDetailsAccess,
			hasTradesInPayoffsAccess,
			hasTitlingAccess,
			hasCancellationsAccess,
		]
	);

	const firstTabWithAccess = Object.values(ETabLabel).find(isTabEnabled) ?? ETabLabel.AllDeals;

	// States
	const [tabSelected, setTabSelected] = useState<TabInfo>(() => ({
		title: firstTabWithAccess,
		index: getTabIndex(firstTabWithAccess),
		disabled: !isTabEnabled(firstTabWithAccess),
	}));

	const tabs = Object.values(ETabLabel)
		.filter(Boolean)
		.map((value) => ({ title: value, index: getTabIndex(value), disabled: !isTabEnabled(value) }));

	/********************************************* QUERIES ************************************************/
	// All Deals
	const {
		data: allDeals = [],
		hasNextPage: hasMoreDeals,
		isLoading: isLoadingAllDeals,
		isFetching: isFetchingAllCITDeals,
	} = useAllDealsPaginatedQuery(selectedPayPeriod, isTabSelected(tabSelected, ETabLabel.AllDeals) && hasAllDealsAccess);

	const {
		data,
		isLoading: isCitLoading,
		isFetching,
	} = useQuery({
		queryKey: ["contractsInTransitExportHistory", locationId, selectedPayPeriod?.cycleId, selectedPayPeriod?.payPeriodId],
		queryFn: async () => {
			return getCitExportHistory(locationId, selectedPayPeriod?.cycleId, selectedPayPeriod?.payPeriodId);
		},
		enabled: and(
			Boolean(locationId),
			Boolean(selectedPayPeriod?.cycleId),
			Boolean(selectedPayPeriod?.payPeriodId),
			hasCITAccess,
			isTabSelected(tabSelected, ETabLabel.CIT)
		),
		staleTime: Infinity,
	});

	const {
		data: citActivities,
		isLoading: isLoadingActivities,
		isFetching: isFetchingActivities,
	} = useCitActivitiesPaginatedQuery(selectedPayPeriod, isTabSelected(tabSelected, ETabLabel.CIT) && hasCITAccess);

	const { data: systemSettings, isLoading: isLoadingCitSystemSettings } = useQuery({
		queryKey: ["citSystemSettings", locationId],
		queryFn: async () => getCITSystemSettings(locationId),
		enabled: !!locationId,
		staleTime: Infinity,
	});

	const { data: financeDeals, isLoading: isLoadingFinanceDeals } = useQuery({
		queryKey: ["financeDeals", locationId, selectedPayPeriod?.cycleId, selectedPayPeriod?.payPeriodId],
		queryFn: async () =>
			getFinanceDeal(locationId ?? "", selectedPayPeriod?.cycleId as string, selectedPayPeriod?.payPeriodId as string),
		enabled: and(
			Boolean(locationId),
			Boolean(selectedPayPeriod?.cycleId),
			Boolean(selectedPayPeriod?.payPeriodId),
			isTabSelected(tabSelected, ETabLabel.FinanceDeals),
			hasFinanceDealsAccess
		),
		staleTime: Infinity,
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const { getSubscription } = useSubscriptions();
	const citSubscription = getSubscription(["contractsInTransit", selectedPayPeriod?._id]);

	citSubscription?.subscriptions?.onExportCitActivities?.setCallback((response: IExportCitActivitiesSubscriptionResponse) => {
		const details = response.data?.onExportCitActivities ?? null;
		if (!details) {
			return;
		}

		const hasError = details.urlDetails?.error || details.journalDetails?.success === false;
		if (hasError) {
			return;
		}

		const { locationId: localLocationId, commissionCycleId, payPeriodId } = details;

		queryClient.invalidateQueries({ queryKey: ["citActivities", localLocationId, `${commissionCycleId}_${payPeriodId}`] });
		queryClient.invalidateQueries({ queryKey: ["contractsInTransit", localLocationId, `${commissionCycleId}_${payPeriodId}`] });
	});

	citSubscription?.subscriptions?.onUpdateCitActivities?.setCallback((response: IUpdateCitActivitiesSubscriptionResponse) => {
		const { locationId, commissionCycleId, payPeriodId } = response.data?.onUpdateCitActivities ?? {};

		if (or(!locationId, !commissionCycleId, !payPeriodId)) {
			return;
		}

		queryClient.invalidateQueries({ queryKey: ["citActivities", locationId, `${commissionCycleId}_${payPeriodId}`] });
		queryClient.invalidateQueries({ queryKey: ["contractsInTransit", locationId, `${commissionCycleId}_${payPeriodId}`] });
	});

	useCitExportHistorySubs({
		locationId: locationId,
		commissionCycleId: selectedPayPeriod?.cycleId,
		payPeriodId: selectedPayPeriod?.payPeriodId,
	});

	const financeDealSubscription = getSubscription(["financeDeal", selectedPayPeriod?._id]);
	financeDealSubscription?.subscriptions?.onFinanceDealsUpdated?.setCallback(
		(response: GraphqlSubscriptionResponse<"onFinanceDealsUpdated", IFinanceDealSubscriptionResponse>) => {
			const updatedDeals: IFinanceDeal[] = response.data?.onFinanceDealsUpdated.financeDeals ?? [];
			if (!updatedDeals?.length) {
				queryClient.invalidateQueries({
					queryKey: ["financeDeals", locationId, selectedPayPeriod?.cycleId, selectedPayPeriod?.payPeriodId],
				});
			}
		}
	);

	const isFetchingCitData = or(isFetching, isFetchingActivities);
	const isLoadingCitData = or(isCitLoading, isLoadingActivities);
	const isLoadingCit = or(isLoadingCitSystemSettings, isLoadingCitData);
	const isLoadingAllCITDeals = or(isLoadingAllDeals, !selectedPayPeriod);

	/********************************************* RENDERING ************************************************/
	return (
		<CitContextProvider receivables={citActivities ?? []} exportHistory={data} citSettings={systemSettings}>
			<AllDealsContextProvider
				commissionCycleId={selectedPayPeriod?.cycleId ?? ""}
				payPeriodId={selectedPayPeriod?.payPeriodId ?? ""}
				locationId={locationId}
				allDeals={allDeals}
			>
				<RoundedBox className="!p-0 !pt-8 !h-auto mb-4">
					<TabSelectable
						tabs={tabs}
						tabsProps={{
							tabClasses: "!min-w-[1100px]",
						}}
						content={[
							<AllDealsAnalyticsSection
								key={ETabLabel.AllDeals}
								data={allDeals}
								selectedPayPeriod={selectedPayPeriod}
								isLoading={isLoadingAllCITDeals}
								isFetching={isFetchingAllCITDeals}
							/>,
							<CitModelAnalytics
								key={ETabLabel.CIT}
								selected={selectedPayPeriod}
								isLoading={isLoadingCit}
								isFetching={isFetchingCitData}
							/>,
							<FinanceDealsAnalyticsSection
								key={ETabLabel.FinanceDeals}
								data={financeDeals}
								selectedPayPeriod={selectedPayPeriod}
								isLoading={isLoadingFinanceDeals}
							/>,
							<FnIDetailsBody
								key={ETabLabel.FinanceDetails}
								periodTreeSelection={selectedPayPeriod}
								isTabSelected={isTabSelected(tabSelected, ETabLabel.FinanceDetails)}
								hasPermission={hasFinanceDetailsAccess}
							/>,
							<TradeInPayoffsBody
								key={ETabLabel.TradesInPayoffs}
								locationId={locationId ?? ""}
								selected={selectedPayPeriod}
								isTabSelected={isTabSelected(tabSelected, ETabLabel.TradesInPayoffs)}
								hasPermission={hasTradesInPayoffsAccess}
							/>,
							<TitlingBody
								key={ETabLabel.Titling}
								periodTreeSelection={selectedPayPeriod}
								isTabSelected={isTabSelected(tabSelected, ETabLabel.Titling)}
								hasPermission={hasTitlingAccess}
							/>,
							<FniProductsBody
								key={ETabLabel.Cancellations}
								selected={selectedPayPeriod}
								filterModel={filterModel}
								setFilterModel={setFilterModel}
								isTabSelected={isTabSelected(tabSelected, ETabLabel.Cancellations)}
								hasPermission={hasCancellationsAccess}
							/>,
						]}
						tabSelected={tabSelected.index}
						onTabChange={(_, tabInfo) => setTabSelected(tabInfo.label)}
					/>
				</RoundedBox>

				{tabSelected.title === ETabLabel.AllDeals && (
					<RoundedBox className="!h-fit !pb-[52px]">
						<AllDealsTableSection
							data={allDeals}
							hasNextPage={hasMoreDeals}
							selectedPayPeriod={selectedPayPeriod}
							isLoading={isLoadingAllCITDeals}
							isFetching={isFetchingAllCITDeals}
						/>
					</RoundedBox>
				)}
				{tabSelected.title === ETabLabel.CIT && (
					<RoundedBox className="!h-fit !pb-[52px]">
						<CitModelHeader
							isLoading={isLoadingCit}
							isFetching={isFetchingCitData}
							locationId={locationId}
							selected={selectedPayPeriod}
							isUpdatable={isUpdatable}
						/>
						<CitModelTable dataPresent={dataPresent} isLoading={isLoadingCit} isFetching={isFetchingCitData} />
					</RoundedBox>
				)}
				{tabSelected.title === ETabLabel.FinanceDeals && (
					<RoundedBox className="!h-fit !pb-[52px]">
						<FinanceDealsBody
							key={ETabLabel.FinanceDeals}
							selected={selectedPayPeriod}
							locationId={locationId ?? ""}
							data={financeDeals}
							isLoading={isLoadingFinanceDeals}
						/>
					</RoundedBox>
				)}
			</AllDealsContextProvider>
		</CitContextProvider>
	);
}
